/**
 * Enhanced authentication utility module
 * Provides token management, automatic refresh, and protected API call wrapper
 */

import { authService } from "./authService";
import { logger } from "../utils/logger";

interface TokenStorage {
  IdToken: string;
  AccessToken: string;
  RefreshToken: string;
  ExpiresAt: number;
}

/**
 * Get current tokens from storage
 */
export const getTokens = (): TokenStorage | null => {
  try {
    const tokens = authService.getStoredTokens();
    const expiresAtStr = localStorage.getItem("adminExpiresAt");
    
    if (!tokens?.IdToken || !expiresAtStr) {
      return null;
    }

    return {
      IdToken: tokens.IdToken,
      AccessToken: tokens.AccessToken,
      RefreshToken: tokens.RefreshToken,
      ExpiresAt: parseInt(expiresAtStr, 10),
    };
  } catch (error) {
    logger.error("Error getting tokens");
    return null;
  }
};

/**
 * Get authorization header with IdToken
 */
export const getAuthHeader = (): { Authorization: string } => {
  const tokens = getTokens();
  if (!tokens?.IdToken) {
    throw new Error("No authentication token available");
  }
  return { Authorization: `Bearer ${tokens.IdToken}` };
};

/**
 * Check if token is expired or expiring soon (within 30 seconds)
 */
export const isTokenExpiringSoon = (): boolean => {
  try {
    const tokens = getTokens();
    if (!tokens) {
      return true;
    }

    const bufferTime = 30 * 1000; // 30 seconds buffer
    return Date.now() > tokens.ExpiresAt - bufferTime;
  } catch (error) {
    logger.error("Error checking token expiration");
    return true;
  }
};

/**
 * Refresh authentication tokens
 */
export const refreshTokens = async (): Promise<void> => {
  try {
    const tokens = getTokens();
    if (!tokens?.RefreshToken) {
      throw new Error("No refresh token available");
    }

    const storedUser = localStorage.getItem("adminUser");
    const user = storedUser ? JSON.parse(storedUser) : null;
    const username = user?.username;

    logger.debug("Refreshing tokens...");
    const result = await authService.refreshToken(tokens.RefreshToken, username);
    
    // Update stored tokens
    const storedTokens = authService.getStoredTokens();
    if (storedTokens) {
      storedTokens.AccessToken = result.accessToken;
      if (result.idToken) {
        storedTokens.IdToken = result.idToken;
      }
      localStorage.setItem("adminTokens", JSON.stringify(storedTokens));
      localStorage.setItem("adminToken", storedTokens.IdToken);
      
      // Update ExpiresAt if provided
      if (result.expiresIn) {
        const expiresAt = Date.now() + result.expiresIn * 1000;
        localStorage.setItem("adminExpiresAt", expiresAt.toString());
      }
    }
    
    logger.debug("Tokens refreshed successfully");
  } catch (error) {
    logger.error("Token refresh failed");
    // Clear tokens and force logout
    authService.logout();
    throw error;
  }
};

/**
 * Wrapper function that handles automatic token refresh and retries
 * Usage:
 * const data = await withAuth(async (headers) => {
 *   const response = await fetch(url, { headers });
 *   return response.json();
 * });
 */
export const withAuth = async <T>(
  fetchFn: (headers: Record<string, string>) => Promise<T>,
  retryOnce = true
): Promise<T> => {
  try {
    // Check if token is expiring soon, refresh proactively
    if (isTokenExpiringSoon()) {
      logger.debug("Token expiring soon, refreshing proactively...");
      try {
        await refreshTokens();
      } catch (refreshError) {
        logger.error("Proactive token refresh failed");
        // If we're in a browser context and have a window, redirect to login
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        throw new Error("Authentication required");
      }
    }

    // Get auth header
    const authHeader = getAuthHeader();
    const headers = {
      "Content-Type": "application/json",
      ...authHeader,
    };

    // Execute the fetch function
    return await fetchFn(headers);
  } catch (error: any) {
    // Check if it's a 401 error and we haven't retried yet
    if (retryOnce && (error?.status === 401 || error?.message?.includes("401"))) {
      logger.debug("Received 401, attempting token refresh and retry...");
      try {
        // Refresh tokens
        await refreshTokens();
        
        // Retry the request with new tokens
        const authHeader = getAuthHeader();
        const headers = {
          "Content-Type": "application/json",
          ...authHeader,
        };
        
        return await fetchFn(headers);
      } catch (retryError) {
        logger.error("Retry after token refresh failed");
        // If we're in a browser context, redirect to login
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        throw new Error("Authentication failed");
      }
    }
    
    throw error;
  }
};

/**
 * Helper to make authenticated fetch requests
 */
export const authenticatedFetch = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  return withAuth(async (headers) => {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}`,
      }));
      const error: any = new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return response.json();
  });
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  const tokens = getTokens();
  return tokens !== null && !isTokenExpiringSoon();
};

